import { Box, Card, Popper } from '@mui/material';
import { Instance } from '@popperjs/core';
import { useThrottleFn } from 'ahooks';
import {
  clearCanvas,
  getContextX,
  highlightRect,
} from 'boardCanvas/CanvasRenderer';
import { ALLOW_WHEN_POPOVER_OPEN } from 'Constants';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { useRecoilValue } from 'recoil';
import {
  BoardType,
  Material,
  Renderable,
  RenderableMetadata,
  ScaledCoordCtr,
  SneezeGuardState,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import MaterialSelector from 'designer/MaterialSelector';
import NumberInput from 'designer/NumberInput';
import { settingsAtom } from 'state/recoilState';
import {
  descaleCoord,
  findRenderableFromCoord,
  normalizeRotation,
  rotateCoord,
} from 'utils/utils';

function BoardCanvasSelectionOverlay<T extends BoardType>(props: {
  height: number;
  width: number;
  board: BoardBase<T>;
  rotation: number;
  onDrag: (snState: SneezeGuardState<T>) => void;
  onDragEnd: () => void;
  onDragStart: () => void;
  onSelectionChange: (selection: null | RenderableMetadata<T>) => void;
  onSelectedFragmentMaterialChange: (
    material: Material,
    renderable: Renderable<T>
  ) => void;
  onSelectedFragmentSizeChange: (
    size: number,
    dimension: 'height' | 'width',
    renderable: Renderable<T>
  ) => void;
  selection: null | RenderableMetadata<T>;
  transitionCount: number;
}) {
  const {
    height,
    width,
    board,
    onDrag,
    onDragEnd,
    onDragStart,
    onSelectionChange,
    onSelectedFragmentMaterialChange,
    onSelectedFragmentSizeChange,
    selection,
    transitionCount,
  } = props;

  const renderables = board.getRenderables();
  const rotation = normalizeRotation(props.rotation);
  const selectedRenderable =
    selection == null ? null : renderables[selection.renderableIndex] ?? null;

  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const settings = useRecoilValue(settingsAtom);

  const popperRef = useRef<Instance>(null);

  const canvasHighlightRef = useRef<HTMLCanvasElement>(null);
  const popoverAnchorRef = useRef<HTMLDivElement>(null);

  const highlightedRenderables = renderables.filter((renderable) => {
    return selectedRenderable == null
      ? false
      : board.getIsRenderableHighlighted(renderable, selectedRenderable);
  });

  const primarySelectionSize =
    selectedRenderable &&
    selectedRenderable.rect[
      selectedRenderable.selectionSizeProp === 'both'
        ? 'width'
        : selectedRenderable.selectionSizeProp
    ];
  const secondarySelectionSize =
    selectedRenderable?.selectionSizeProp === 'both'
      ? selectedRenderable.rect.height
      : null;
  const selectionMaterial = selectedRenderable && selectedRenderable.material;

  useLayoutEffect(() => {
    canvasHighlightRef.current && clearCanvas(canvasHighlightRef.current);

    if (canvasHighlightRef.current && highlightedRenderables) {
      for (const highlightedRenderable of highlightedRenderables) {
        highlightRect(
          canvasHighlightRef.current,
          highlightedRenderable,
          settings.pxPerInch
        );
      }

      const ctx = getContextX(canvasHighlightRef.current);
      ctx.setLineDash([2, 2]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'white';
      ctx.stroke();
    }
  });

  // The popoverRect can change while the selection popover
  // is open, so update the popover position when the rect changes.
  useLayoutEffect(() => {
    popperRef.current?.update();
  }, [transitionCount]);

  useLayoutEffect(() => {
    setIsPopoverOpen(false);
  }, [rotation]);

  const [, dragRef] = useDrag(() => ({
    type: 'item',
    item() {
      setIsPopoverOpen(false);
      onDragStart();

      return {};
    },
    end() {
      setIsPopoverOpen(true);
      onDragEnd();
    },
  }));

  const onHover = useThrottleFn(
    (_item: any, monitor: DropTargetMonitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();

      if (delta == null) {
        return;
      }

      onDrag({
        dragDelta: {
          x: delta.x / settings.pxPerInch,
          y: delta.y / settings.pxPerInch,
        },
        selection: null, //selection,
      });
    },
    { wait: 100 }
  ).run;

  const [, dropRef] = useDrop(() => ({
    accept: 'item',
    hover: onHover,
  }));

  const onCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!(event.target instanceof HTMLCanvasElement)) {
      return;
    }

    const boundingRect = event.target?.getBoundingClientRect();

    const coord = descaleCoord(
      rotateCoord(
        ScaledCoordCtr({
          top: event.clientY - boundingRect.top,
          left: event.clientX - boundingRect.left,
        }),
        rotation,
        { height, width }
      ),
      settings.pxPerInch
    );

    const renderableIndex = findRenderableFromCoord(coord, renderables);
    const renderable = renderables[renderableIndex];

    if (renderable == null) {
      onSelectionChange(null);
      setIsPopoverOpen(false);
      return;
    }

    onSelectionChange(renderable.metadata);
    setIsPopoverOpen(true);

    popperRef.current?.update();
  };

  useEffect(() => {
    function documentMouseDownHandler(event: MouseEvent) {
      if (
        isPopoverOpen &&
        canvasHighlightRef.current &&
        event.target !== canvasHighlightRef.current
      ) {
        const allowed = document.body.querySelectorAll(
          '.' + ALLOW_WHEN_POPOVER_OPEN
        );
        const isAllowed = Array.from(allowed).every(
          (node) => !node.contains(event.target as Node)
        );

        if (!isAllowed) {
          setTimeout(() => {
            popperRef.current?.update();
          }, 300);
        }

        if (isAllowed) {
          onSelectionChange(null);
          setIsPopoverOpen(false);
        }
      }
    }

    function documentKeyDownHandler(event: KeyboardEvent) {
      if (isPopoverOpen && event.key === 'Escape') {
        onSelectionChange(null);
        setIsPopoverOpen(false);
      }
    }

    document.documentElement.addEventListener(
      'mousedown',
      documentMouseDownHandler
    );

    document.documentElement.addEventListener(
      'keydown',
      documentKeyDownHandler
    );

    return () => {
      document.documentElement.removeEventListener(
        'mousedown',
        documentMouseDownHandler
      );

      document.documentElement.removeEventListener(
        'keydown',
        documentKeyDownHandler
      );
    };
  }, [isPopoverOpen, onSelectionChange]);

  const popoverRect = selectedRenderable && selectedRenderable.rect;

  return (
    <>
      <div ref={dropRef} style={{ fontSize: 0 }}>
        <canvas
          height={height}
          width={width}
          ref={canvasHighlightRef}
          onMouseDown={onCanvasClick}
        />
      </div>

      <div
        className={ALLOW_WHEN_POPOVER_OPEN}
        style={{
          position: 'absolute',
          top: popoverRect ? popoverRect.top * settings.pxPerInch : 0,
          left: popoverRect ? popoverRect.left * settings.pxPerInch : 0,
          height: popoverRect ? popoverRect.height * settings.pxPerInch : 0,
          width: popoverRect ? popoverRect.width * settings.pxPerInch : 0,
        }}
        ref={popoverAnchorRef}
      >
        <div ref={dragRef} style={{ position: 'absolute', inset: 0 }} />
      </div>

      <Popper
        open={isPopoverOpen && popoverRect != null}
        anchorEl={popoverAnchorRef.current}
        placement={rotation === 0 || rotation === 180 ? 'left' : 'bottom'}
        popperRef={popperRef}
      >
        <Card className={ALLOW_WHEN_POPOVER_OPEN}>
          <Box sx={{ display: 'flex', p: 2 }}>
            <Box sx={{ pr: 2 }}>
              {primarySelectionSize != null && (
                <NumberInput
                  id="selected-fragment-size"
                  label={
                    selectedRenderable?.selectionSizeProp === 'both'
                      ? 'Width'
                      : 'Size'
                  }
                  size={64}
                  step={1 / 8}
                  value={primarySelectionSize}
                  onChange={(size) =>
                    selectedRenderable &&
                    onSelectedFragmentSizeChange(
                      size,
                      selectedRenderable?.selectionSizeProp === 'both'
                        ? 'width'
                        : selectedRenderable.selectionSizeProp,
                      selectedRenderable
                    )
                  }
                ></NumberInput>
              )}
            </Box>
            {selectedRenderable?.selectionSizeProp === 'both' && (
              <Box sx={{ pr: 2 }}>
                {secondarySelectionSize != null && (
                  <NumberInput
                    id="selected-fragment-size"
                    label="height"
                    size={64}
                    step={1 / 8}
                    value={secondarySelectionSize}
                    onChange={(size) =>
                      selectedRenderable &&
                      onSelectedFragmentSizeChange(
                        size,
                        'height',
                        selectedRenderable
                      )
                    }
                  ></NumberInput>
                )}
              </Box>
            )}
            {selectionMaterial && (
              <MaterialSelector
                value={selectionMaterial}
                onChange={(material) =>
                  selectedRenderable &&
                  onSelectedFragmentMaterialChange(material, selectedRenderable)
                }
                MenuProps={{
                  PopoverClasses: { paper: ALLOW_WHEN_POPOVER_OPEN },
                }}
              ></MaterialSelector>
            )}
          </Box>
        </Card>
      </Popper>
    </>
  );
}

export default BoardCanvasSelectionOverlay;
